import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuArrowLeft,
  LuArrowRight,
  LuClock,
  LuDownload,
  LuFileText,
  LuLayers,
  LuMessageSquareHeart,
  LuStar,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  feedbackExtractMessage,
  fetchMyFeedbackById,
} from "../../services/platformFeedback.service";
import "../../assets/styles/my_dispute_detail.scss";

const TIMELINE_STEPS = [
  { key: "submitted", label: "Submitted", match: ["new"] },
  {
    key: "under_review",
    label: "Under Review",
    match: ["under_review", "assigned", "waiting_user"],
  },
  { key: "in_progress", label: "In Progress", match: ["in_progress"] },
  { key: "resolved", label: "Resolved", match: ["resolved"] },
  { key: "closed", label: "Closed", match: ["closed"] },
];

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatStatus(status, label) {
  if (label) return label;
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusClass(status) {
  const normalized = String(status || "").toLowerCase();
  return `mdd-status mdd-status--${normalized.replace(/\s+/g, "_")}`;
}

function isImageMime(mime, name = "") {
  if (mime && String(mime).startsWith("image/")) return true;
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(String(name || ""));
}

function currentTimelineIndex(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "closed") return 4;
  if (normalized === "resolved") return 3;
  if (normalized === "in_progress") return 2;
  if (["under_review", "assigned", "waiting_user"].includes(normalized)) return 1;
  if (normalized === "new") return 0;
  return 0;
}

function collectAttachments(feedbackAttachments) {
  return (feedbackAttachments || []).map((file) => ({
    id: `att-${file.id}`,
    name: file.fileName || "Attachment",
    path: file.filePath,
    mime: file.fileMime,
    createdAt: file.createdAt,
  }));
}

export default function MyFeedbackDetailPage() {
  const params = useParams();
  const feedbackId = params.id || params.feedbackId;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [showAllAttachments, setShowAllAttachments] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMyFeedbackById(feedbackId);
      if (!data?.feedback) throw new Error("Feedback not found.");
      setDetail(data);
    } catch (error) {
      toast.error(feedbackExtractMessage(error, "Failed to load feedback details."));
      navigate("/dashboard/help-center/my-feedback");
    } finally {
      setLoading(false);
    }
  }, [feedbackId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const feedback = detail?.feedback;
  const activities = useMemo(
    () => (Array.isArray(detail?.activities) ? detail.activities : []),
    [detail?.activities],
  );
  const attachments = useMemo(
    () => collectAttachments(detail?.attachments),
    [detail?.attachments],
  );
  const visibleAttachments = showAllAttachments ? attachments : attachments.slice(0, 2);
  const activeStep = currentTimelineIndex(feedback?.status);
  const activityItems = showAllActivity ? activities : activities.slice(0, 5);
  const rating = Math.max(0, Math.min(5, Math.trunc(Number(feedback?.rating) || 0)));

  if (loading || !feedback) {
    return (
      <div className="mdd-page">
        <p className="mdd-loading">Loading feedback details...</p>
      </div>
    );
  }

  return (
    <div className="mdd-page">
      <nav className="mdd-breadcrumb" aria-label="Breadcrumb">
        <Link to="/home">Home</Link>
        <span aria-hidden>&gt;</span>
        <Link to="/help-center">Help Center</Link>
        <span aria-hidden>&gt;</span>
        <Link to="/dashboard/help-center/my-feedback">My Feedback</Link>
        <span aria-hidden>&gt;</span>
        <span aria-current="page">Feedback Details</span>
      </nav>

      <Link to="/dashboard/help-center/my-feedback" className="mdd-back">
        <LuArrowLeft aria-hidden /> Back to My Feedback
      </Link>

      <header className="mdd-head">
        <h1>Feedback Details</h1>
      </header>

      <section className="mdd-meta">
        <div className="mdd-meta__item">
          <span>Status</span>
          <strong className={statusClass(feedback.status)}>
            {formatStatus(feedback.status, feedback.statusLabel)}
          </strong>
        </div>
        <div className="mdd-meta__item">
          <span>Feedback ID</span>
          <strong>{feedback.feedbackNumber}</strong>
        </div>
        <div className="mdd-meta__item">
          <span>Type</span>
          <strong>{feedback.feedbackType || "—"}</strong>
        </div>
        <div className="mdd-meta__item">
          <span>Submitted On</span>
          <strong>{formatDateTime(feedback.createdAt)}</strong>
        </div>
        <div className="mdd-meta__item">
          <span>Last Updated</span>
          <strong>{formatDateTime(feedback.updatedAt)}</strong>
        </div>
      </section>

      <div className="mdd-layout">
        <div className="mdd-main">
          <section className="mdd-card">
            <header className="mdd-card__head">
              <div>
                <h2>Feedback Summary</h2>
                <p>Here is a summary of the feedback you submitted.</p>
              </div>
            </header>
            <div className="mdd-summary-grid">
              <article>
                <span className="mdd-summary-grid__icon mdd-summary-grid__icon--blue">
                  <LuMessageSquareHeart aria-hidden />
                </span>
                <div>
                  <small>Type</small>
                  <strong>{feedback.feedbackType || "—"}</strong>
                </div>
              </article>
              <article>
                <span className="mdd-summary-grid__icon mdd-summary-grid__icon--purple">
                  <LuFileText aria-hidden />
                </span>
                <div>
                  <small>Subject</small>
                  <strong>{feedback.subject || "—"}</strong>
                </div>
              </article>
              <article>
                <span className="mdd-summary-grid__icon mdd-summary-grid__icon--green">
                  <LuLayers aria-hidden />
                </span>
                <div>
                  <small>Area</small>
                  <strong>{feedback.areaLabel || "—"}</strong>
                </div>
              </article>
              <article>
                <span className="mdd-summary-grid__icon mdd-summary-grid__icon--red">
                  <LuStar aria-hidden />
                </span>
                <div>
                  <small>Rating</small>
                  <strong>
                    {rating} / 5
                    <span style={{ marginLeft: 8, color: "#f59e0b" }}>
                      {Array.from({ length: rating }, (_, i) => (
                        <LuStar key={i} aria-hidden style={{ width: 14, height: 14 }} />
                      ))}
                    </span>
                  </strong>
                </div>
              </article>
            </div>
            <div className="mdd-description">
              <h3>Description</h3>
              <p>{feedback.description || "No description provided."}</p>
            </div>
          </section>

          {feedback.resolutionNotes ? (
            <section className="mdd-card">
              <header className="mdd-card__head">
                <div>
                  <h2>Resolution Notes</h2>
                  <p>Outcome shared by the Trocanet team.</p>
                </div>
              </header>
              <div className="mdd-description">
                <p>{feedback.resolutionNotes}</p>
                {(feedback.resolvedAt || feedback.closedAt) && (
                  <p className="mdd-muted" style={{ marginTop: 12 }}>
                    {feedback.resolvedAt
                      ? `Resolved on ${formatDateTime(feedback.resolvedAt)}`
                      : `Closed on ${formatDateTime(feedback.closedAt)}`}
                  </p>
                )}
              </div>
            </section>
          ) : null}
        </div>

        <aside className="mdd-side">
          <section className="mdd-card">
            <h2>Feedback Timeline</h2>
            <ol className="mdd-timeline">
              {TIMELINE_STEPS.map((step, index) => {
                const done = index < activeStep;
                const current = index === activeStep;
                const activity = activities.find((item) =>
                  String(item.title || "")
                    .toLowerCase()
                    .includes(step.label.toLowerCase().split(" ")[0]),
                );
                return (
                  <li
                    key={step.key}
                    className={`mdd-timeline__item${done ? " is-done" : ""}${current ? " is-current" : ""}`}
                  >
                    <span className="mdd-timeline__dot" aria-hidden>
                      {done || current ? "✓" : index + 1}
                    </span>
                    <div>
                      <strong>{step.label}</strong>
                      <small>
                        {current
                          ? formatDateTime(feedback.updatedAt)
                          : activity
                            ? formatDateTime(activity.createdAt)
                            : done
                              ? "Completed"
                              : "Pending"}
                      </small>
                    </div>
                  </li>
                );
              })}
            </ol>
            {activities.length > 0 ? (
              <button
                type="button"
                className="mdd-text-link"
                onClick={() => setShowAllActivity((value) => !value)}
              >
                {showAllActivity ? "Hide Activity" : "View All Activity"}
              </button>
            ) : null}
            {showAllActivity && activityItems.length > 0 ? (
              <ul className="mdd-activity-list">
                {activityItems.map((item) => (
                  <li key={item.id}>
                    <strong>{item.title}</strong>
                    <span>{item.remarks || item.performedByName || "—"}</span>
                    <small>{formatDateTime(item.createdAt)}</small>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          <section className="mdd-card">
            <h2>Status</h2>
            <div className="mdd-support">
              <span className="mdd-label">Current Status</span>
              <strong className={statusClass(feedback.status)}>
                {formatStatus(feedback.status, feedback.statusLabel)}
              </strong>
              <div className="mdd-hours">
                <span className="mdd-label">Last Update</span>
                <strong>
                  <LuClock aria-hidden style={{ marginRight: 6 }} />
                  {formatDateTime(feedback.updatedAt)}
                </strong>
              </div>
            </div>
          </section>

          <section className="mdd-card">
            <h2>Attachments ({attachments.length})</h2>
            {attachments.length === 0 ? (
              <p className="mdd-muted">No attachments.</p>
            ) : (
              <ul className="mdd-files">
                {visibleAttachments.map((file) => (
                  <li key={file.id}>
                    <span className="mdd-files__icon" aria-hidden>
                      {isImageMime(file.mime, file.name) ? <LuLayers /> : <LuFileText />}
                    </span>
                    <div>
                      <strong title={file.name}>{file.name}</strong>
                      <small>{formatDateTime(file.createdAt)}</small>
                    </div>
                    {file.path ? (
                      <a
                        href={resolvePublicFileUrl(file.path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mdd-icon-round"
                        title="Download"
                      >
                        <LuDownload aria-hidden />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {attachments.length > 2 ? (
              <button
                type="button"
                className="mdd-text-link"
                onClick={() => setShowAllAttachments((value) => !value)}
              >
                {showAllAttachments ? "Show Less" : "View All Attachments"}{" "}
                <LuArrowRight aria-hidden />
              </button>
            ) : null}
          </section>
        </aside>
      </div>
    </div>
  );
}
